import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Leaf, X, ChevronLeft, Play, Pause, RotateCcw } from 'lucide-react';
import { GROUNDING_TECHNIQUES } from '../data/content';

/* ---------- Box-breathing animator (used for `box` exercise) ---------- */
function BoxBreather() {
  const PHASES = [
    { label: 'Breathe in', sub: 'Through your nose', dur: 4 },
    { label: 'Hold', sub: 'Gently', dur: 4 },
    { label: 'Breathe out', sub: 'Through your mouth', dur: 4 },
    { label: 'Hold', sub: 'Empty lungs', dur: 4 },
  ];
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [count, setCount] = useState(PHASES[0].dur);
  const [cycles, setCycles] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!running) return undefined;
    intervalRef.current = setInterval(() => {
      setCount((c) => {
        if (c > 1) return c - 1;
        // advance phase
        setPhaseIdx((p) => {
          const next = (p + 1) % PHASES.length;
          if (next === 0) setCycles((cy) => cy + 1);
          return next;
        });
        return PHASES[(phaseIdx + 1) % PHASES.length].dur;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, phaseIdx]);

  const reset = () => {
    setRunning(false);
    setPhaseIdx(0);
    setCount(PHASES[0].dur);
    setCycles(0);
  };

  const phase = PHASES[phaseIdx];
  // scale: 0.75 when exhaling/hold-empty, 1 when inhaling/hold-full
  const inflated = phaseIdx === 0 || phaseIdx === 1;

  return (
    <div className="flex flex-col items-center pt-2 pb-4">
      <div
        className="relative flex items-center justify-center"
        style={{ width: 220, height: 220 }}
      >
        {/* Outer ring */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'conic-gradient(from -90deg, var(--tf-purple) 0deg, var(--tf-pink) 180deg, var(--tf-blue) 360deg)',
            opacity: 0.18,
            filter: 'blur(2px)',
          }}
        />
        {/* Breathing circle */}
        <div
          className="absolute rounded-full"
          style={{
            width: '70%',
            height: '70%',
            background:
              'radial-gradient(circle at 35% 30%, #fff 0%, var(--tf-pink-soft) 40%, var(--tf-purple-light) 100%)',
            transform: `scale(${inflated ? 1.18 : 0.72})`,
            transition: 'transform 4s cubic-bezier(0.45, 0.05, 0.55, 0.95)',
            boxShadow: '0 18px 60px rgba(139,123,177,0.35)',
          }}
        />
        <div className="relative z-10 text-center">
          <p
            className="font-heading"
            style={{ fontSize: 26, color: 'var(--tf-text)', lineHeight: 1.05 }}
          >
            {phase.label}
          </p>
          <p
            className="font-body mt-1"
            style={{ fontSize: 12, color: 'var(--tf-text-secondary)' }}
          >
            {phase.sub}
          </p>
          <p
            className="font-heading mt-3"
            style={{
              fontSize: 44,
              color: 'var(--tf-purple)',
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {count}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button
          type="button"
          onClick={() => setRunning((r) => !r)}
          data-testid="breather-toggle"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold"
          style={{
            background: 'var(--tf-purple)',
            color: '#fff',
            fontSize: 13.5,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {running ? <Pause size={15} /> : <Play size={15} fill="#fff" />}
          {running ? 'Pause' : cycles > 0 ? 'Continue' : 'Start'}
        </button>
        <button
          type="button"
          onClick={reset}
          data-testid="breather-reset"
          className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full font-bold"
          style={{
            background: '#fff',
            color: 'var(--tf-purple)',
            fontSize: 13,
            border: '1px solid var(--tf-purple-light)',
            cursor: 'pointer',
          }}
        >
          <RotateCcw size={14} />
          Reset
        </button>
      </div>

      <p
        className="font-body mt-4"
        style={{ fontSize: 12, color: 'var(--tf-text-muted)' }}
      >
        Cycles completed: <strong style={{ color: 'var(--tf-text-secondary)' }}>{cycles}</strong>
        {' · '}Aim for 4–8 · Stop sooner if uncomfortable.
      </p>
    </div>
  );
}

/* ---------- Step list (used for the other 3 techniques) ---------- */
function StepList({ steps }) {
  return (
    <div className="flex flex-col gap-3 mt-2">
      {steps.map((s, i) => (
        <div key={i} className="flex gap-3 items-start">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              background: 'var(--tf-purple-light)',
              color: 'var(--tf-purple-dark)',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {i + 1}
          </div>
          <p
            className="font-body flex-1"
            style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--tf-text-secondary)' }}
          >
            {s}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ---------- Main FAB + modal ---------- */
export default function GroundingFAB() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // ESC closes
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (activeId) setActiveId(null);
        else setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, activeId]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  const closeAll = useCallback(() => {
    setActiveId(null);
    setOpen(false);
  }, []);

  // Hide FAB on grounding page itself (it's already the dedicated page)
  if (pathname.startsWith('/app/grounding')) return null;

  const active = activeId
    ? GROUNDING_TECHNIQUES.find((g) => g.id === activeId)
    : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="grounding-fab"
        aria-label="Open grounding exercises"
        className="tf-no-print fixed z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full font-bold"
        style={{
          right: 16,
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
          background: '#fff',
          color: 'var(--tf-purple)',
          border: '1.5px solid var(--tf-purple-light)',
          boxShadow: '0 8px 24px rgba(139,123,177,0.18)',
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        <Leaf size={16} />
        <span>Ground me</span>
      </button>

      {open && (
        <div
          className="tf-no-print fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          data-testid="grounding-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Grounding exercises"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close grounding exercises"
            onClick={closeAll}
            className="absolute inset-0"
            style={{
              background: 'rgba(42, 42, 53, 0.55)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              border: 'none',
              cursor: 'pointer',
            }}
            data-testid="grounding-backdrop"
          />

          <div
            className="relative w-full sm:max-w-md max-h-[88vh] overflow-y-auto"
            style={{
              background: 'var(--tf-bg)',
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
              borderBottomLeftRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? 28 : 0,
              borderBottomRightRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? 28 : 0,
              boxShadow: '0 -20px 60px rgba(42,42,53,0.25)',
              animation: 'tf-fade-up 0.25s ease-out',
            }}
          >
            {/* Header */}
            <div
              className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5"
              style={{
                background: 'rgba(253,252,254,0.95)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid var(--tf-card-border)',
              }}
            >
              {active ? (
                <button
                  type="button"
                  onClick={() => setActiveId(null)}
                  className="inline-flex items-center gap-1 font-bold"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--tf-text)',
                    fontSize: 13.5,
                    cursor: 'pointer',
                  }}
                  data-testid="grounding-back"
                >
                  <ChevronLeft size={18} /> All techniques
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <Leaf size={16} color="var(--tf-purple)" />
                  <span
                    className="font-bold"
                    style={{ color: 'var(--tf-text)', fontSize: 14 }}
                  >
                    Ground me
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={closeAll}
                aria-label="Close"
                data-testid="grounding-close"
                className="w-9 h-9 rounded-full inline-flex items-center justify-center"
                style={{
                  background: 'var(--tf-bg-secondary)',
                  border: 'none',
                  color: 'var(--tf-text)',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5 pb-7">
              {!active && (
                <>
                  <p className="eyebrow">FEELING OVERWHELMED?</p>
                  <h2
                    className="font-heading mt-2"
                    style={{ fontSize: 22, lineHeight: 1.15, color: 'var(--tf-text)' }}
                  >
                    You&apos;re safe right now.
                  </h2>
                  <p
                    className="font-body mt-2"
                    style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
                  >
                    Pick whichever feels easiest. There is no wrong way.
                  </p>

                  <div className="flex flex-col gap-2.5 mt-5">
                    {GROUNDING_TECHNIQUES.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setActiveId(g.id)}
                        data-testid={`fab-technique-${g.id}`}
                        className="text-left rounded-2xl p-4 flex items-center gap-3 transition-colors"
                        style={{
                          background: '#fff',
                          border: '1px solid var(--tf-card-border)',
                          cursor: 'pointer',
                        }}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{
                            background: 'var(--tf-bg-secondary)',
                            color: 'var(--tf-purple)',
                          }}
                        >
                          <Leaf size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="font-heading"
                            style={{ fontSize: 15.5, color: 'var(--tf-text)' }}
                          >
                            {g.title}
                          </p>
                          <p
                            className="font-body"
                            style={{
                              fontSize: 11.5,
                              color: 'var(--tf-text-muted)',
                              marginTop: 1,
                              letterSpacing: '0.02em',
                            }}
                          >
                            {g.duration}
                          </p>
                        </div>
                        <span
                          className="font-bold"
                          style={{ color: 'var(--tf-purple)', fontSize: 13 }}
                        >
                          Start →
                        </span>
                      </button>
                    ))}
                  </div>

                  <p
                    className="font-body mt-5 text-center"
                    style={{ fontSize: 11.5, color: 'var(--tf-text-muted)', lineHeight: 1.55 }}
                  >
                    If you are in crisis, call Samaritans on{' '}
                    <a href="tel:116123" className="tf-link">
                      116 123
                    </a>{' '}
                    (24/7) — or 999 in immediate danger.
                  </p>
                </>
              )}

              {active && (
                <>
                  <p
                    className="font-body font-bold"
                    style={{
                      fontSize: 10.5,
                      letterSpacing: '0.18em',
                      color: 'var(--tf-purple)',
                    }}
                  >
                    {active.duration.toUpperCase()}
                  </p>
                  <h2
                    className="font-heading mt-1"
                    style={{ fontSize: 24, lineHeight: 1.15, color: 'var(--tf-text)' }}
                  >
                    {active.title}
                  </h2>

                  {active.id === 'box' ? (
                    <BoxBreather />
                  ) : (
                    <StepList steps={active.steps} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
